import React, { useState } from "react";
import { PhoneInput } from "../components/ui/phone-input";
import { isValidPhoneNumber } from "react-phone-number-input";
import { useAuth } from "../components/context/auth/AuthState";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import OtpVerification from "../components/auth/OtpVerification";
import { AlertCircle, CheckCircle2, Leaf } from "lucide-react";

const SignUpPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { signup } = useAuth();

  const [status, setStatus] = useState({ msg: "", type: "success" });
  const [phno, setPhno] = useState({ phone: "", valid: false });
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    dob: "",
    gender: "",
    address: {
      locality: "",
      city: "",
      state: "",
      country: "",
      pincode: "",
    },
    height: "",
    weight: "",
  });
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState("");

  const handleChangePhno = (value) => {
    setPhno({
      phone: value,
      valid: value ? isValidPhoneNumber(value) : false
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes("address.")) {
      const addressField = name.split(".")[1];
      setFormData((prev) => ({
        ...prev,
        address: { ...prev.address, [addressField]: value },
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ msg: "", type: "success" });

    if (!phno.valid) {
      setStatus({ msg: "Invalid Phone Number", type: "error" });
      setLoading(false);
      return;
    }

    if (otp.length !== 6) {
      setStatus({ msg: "Enter the 6-digit WhatsApp OTP", type: "error" });
      setLoading(false);
      return;
    }

    try {
      const userData = {
        ...formData,
        phone: phno.phone,
        otp,
      };
      await signup(userData);
      navigate(id ? `/join/${id}` : "/", { replace: true });
    } catch (error) {
      setStatus({
        msg: error.message || "Signup Failed",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="w-full min-h-screen lg:grid lg:grid-cols-2 overflow-hidden">
      {/* Left Side - Form */}
      <div className="flex justify-center py-12 px-4 sm:px-6 lg:px-8 bg-white overflow-y-auto h-screen">
        <div className="w-full max-w-xl space-y-8">
          <div className="text-center">
            <div className="flex justify-center mb-4">
              <div className="h-12 w-12 bg-emerald-100 rounded-full flex items-center justify-center">
                <Leaf className="h-6 w-6 text-emerald-600" />
              </div>
            </div>
            <h2 className="mt-2 text-3xl font-bold tracking-tight text-gray-900">
              Create an account
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              Join the community and start your journey
            </p>
          </div>

          <form className="mt-8 space-y-6" onSubmit={handleSignup}>
            {status.msg && (
              <Alert
                variant={status.type === "error" ? "destructive" : "default"}
                className={`border ${status.type === "error" ? "border-red-200 bg-red-50" : "border-emerald-200 bg-emerald-50"}`}
              >
                {status.type === "error" ? (
                  <AlertCircle className="h-4 w-4 text-red-600" />
                ) : (
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                )}
                <AlertDescription className={status.type === "error" ? "text-red-800" : "text-emerald-800"}>
                  {status.msg}
                </AlertDescription>
              </Alert>
            )}

            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    name="firstName"
                    placeholder="John"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    name="lastName"
                    placeholder="Doe"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">Phone Number</Label>
                <PhoneInput
                  value={phno.phone}
                  onChange={handleChangePhno}
                  defaultCountry="IN"
                  className="w-full"
                />
              </div>

              <OtpVerification
                phone={phno.phone}
                phoneValid={phno.valid}
                purpose="signup"
                otp={otp}
                onOtpChange={setOtp}
                onError={(msg) => msg && setStatus({ msg, type: "error" })}
              />

              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="Create a strong password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="dob">Date of Birth</Label>
                  <Input
                    id="dob"
                    name="dob"
                    type="date"
                    value={formData.dob}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="gender">Gender</Label>
                  <Select
                    value={formData.gender}
                    onValueChange={(value) => handleChange({ target: { name: "gender", value } })}
                    required
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Male">Male</SelectItem>
                      <SelectItem value="Female">Female</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="locality">Address</Label>
                <Input
                  id="locality"
                  name="address.locality"
                  placeholder="Street Address / Locality"
                  value={formData.address.locality}
                  onChange={handleChange}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="city">City</Label>
                  <Input
                    id="city"
                    name="address.city"
                    placeholder="City"
                    value={formData.address.city}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State</Label>
                  <Input
                    id="state"
                    name="address.state"
                    placeholder="State"
                    value={formData.address.state}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    name="address.country"
                    placeholder="Country"
                    value={formData.address.country}
                    onChange={handleChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pincode">Pincode</Label>
                  <Input
                    id="pincode"
                    name="address.pincode"
                    placeholder="ZIP / Pin"
                    value={formData.address.pincode}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="height">Height (cm)</Label>
                  <Input
                    id="height"
                    name="height"
                    type="number"
                    placeholder="175"
                    value={formData.height}
                    onChange={handleChange}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="weight">Weight (kg)</Label>
                  <Input
                    id="weight"
                    name="weight"
                    type="number"
                    placeholder="70"
                    value={formData.weight}
                    onChange={handleChange}
                  />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-2.5 h-auto text-base font-medium shadow-sm transition-all duration-200"
              disabled={loading}
            >
              {loading ? "Creating account..." : "Create Account"}
            </Button>

            <div className="text-center text-sm">
              <span className="text-gray-500">Already have an account? </span>
              <Link
                to={id ? `/login/${id}` : "/login"}
                className="font-medium text-emerald-600 hover:text-emerald-500"
              >
                Sign in
              </Link>
            </div>
          </form>
        </div>
      </div>

      {/* Right Side - Image */}
      <div className="hidden lg:block relative h-full bg-gray-50">
        <img
          className="absolute inset-0 h-full w-full object-cover"
          src="https://images.unsplash.com/photo-1505576399279-565b52d4ac71?q=80&w=1920&auto=format&fit=crop"
          alt="Healthy food and lifestyle"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent flex items-end p-12">
          <div className="text-white space-y-4 max-w-lg">
            <h3 className="text-3xl font-bold">Eat Well, Live Better</h3>
            <p className="text-lg text-gray-100">
              Discover nutritious recipes, track your meals, and achieve your dietary goals with ease.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignUpPage;
